import { type MouseEvent, type ReactNode, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import Button from '@rio-cloud/rio-uikit/Button';
import ButtonToolbar from '@rio-cloud/rio-uikit/ButtonToolbar';
import Dialog from '@rio-cloud/rio-uikit/Dialog';
import Notification from '@rio-cloud/rio-uikit/Notification';
import useClipboard from '@rio-cloud/rio-uikit/useClipboard';

import packageJson from '../../../package.json';
import { ReportDialog } from '../components/ReportDialog';
import { getFigmaPayloadFromElement } from './exportSelectedElementForFigma';
import { getWebHtmlFromElement } from './exportSelectedElementForWeb';

type HoverFrame = {
    left: number;
    top: number;
    width: number;
    height: number;
};

type SelectionOverlayProps = {
    hoverFrame: HoverFrame | null;
    selectionFrame: HoverFrame | null;
    onClick: (event: MouseEvent<HTMLDivElement>) => void;
    onMouseMove: (event: MouseEvent<HTMLDivElement>) => void;
};

type PlaygroundDetachDialogProps = {
    children: ReactNode;
    contentClassName: string;
    onClose: () => void;
    reportContext?: PlaygroundReportContext | null;
    show: boolean;
};

type SelectionMode = 'figma' | 'web';

export type PlaygroundReportContext = {
    pageUrl: string;
    sectionUrl?: string;
};

const figmaPluginSupportUrl = 'https://my-rio.slack.com/archives/C8T2WSZJ9';
const figmaPluginDownloadFileName = 'uikit-figma-plugin.zip';
const isDev = import.meta.env.DEV;

const getFigmaPluginDownloadUrl = () => {
    const selectedVersion = new URLSearchParams(window.location.search).get('version') || packageJson.version;

    if (isDev) {
        return `${window.location.origin}/uikit-figma-plugin/${figmaPluginDownloadFileName}`;
    }

    return `https://uikit.developers.rio.cloud/${selectedVersion}/${figmaPluginDownloadFileName}`;
};

const getTargetElement = (target: EventTarget | null) => {
    if (target instanceof HTMLElement) {
        return target;
    }

    if (target instanceof Element) {
        return target.closest<HTMLElement>('*');
    }

    return null;
};

const getPreferredElement = (
    target: EventTarget | null,
    selectionRoot: HTMLElement | null,
    ignoredElements: HTMLElement[] = []
) => {
    const targetElement = getTargetElement(target);
    const detachedViewElement = selectionRoot?.classList.contains('playground-content')
        ? selectionRoot.querySelector<HTMLElement>(':scope > .detached-view')
        : null;

    if (targetElement === selectionRoot && detachedViewElement) {
        return detachedViewElement;
    }

    if (
        !targetElement ||
        !selectionRoot?.contains(targetElement) ||
        ignoredElements.some(ignoredElement => ignoredElement.contains(targetElement))
    ) {
        return null;
    }

    const inputGroupElement = targetElement.closest<HTMLElement>('.input-group');
    const outerInputGroupElement = inputGroupElement?.classList.contains('ClearableInput')
        ? inputGroupElement.parentElement?.closest<HTMLElement>('.input-group')
        : null;
    const selectedInputGroupElement = outerInputGroupElement ?? inputGroupElement;

    if (
        selectedInputGroupElement &&
        selectedInputGroupElement !== selectionRoot &&
        selectionRoot.contains(selectedInputGroupElement)
    ) {
        return selectedInputGroupElement;
    }

    const selectors = ['.btn', '.label', '.alert', '.panel', '.card', '[data-mcp-visual-id]'];

    const preferredElement = selectors
        .map(selector => targetElement.closest(selector))
        .find(
            element => element instanceof HTMLElement && element !== selectionRoot && selectionRoot.contains(element)
        );

    if (preferredElement instanceof HTMLElement) {
        return preferredElement;
    }

    const hoveredDetachedViewElement = targetElement.closest<HTMLElement>('.detached-view');

    if (
        hoveredDetachedViewElement === targetElement &&
        hoveredDetachedViewElement !== selectionRoot &&
        selectionRoot.contains(hoveredDetachedViewElement)
    ) {
        return hoveredDetachedViewElement;
    }

    return targetElement === document.body || targetElement === document.documentElement ? null : targetElement;
};

const getElementFromPoint = (
    x: number,
    y: number,
    selectionRoot: HTMLElement | null,
    ignoredElements: HTMLElement[] = []
) => {
    const previousPointerEvents = ignoredElements.map(element => element.style.pointerEvents);

    ignoredElements.forEach(element => {
        element.style.pointerEvents = 'none';
    });

    const elements = document.elementsFromPoint(x, y);

    ignoredElements.forEach((element, index) => {
        element.style.pointerEvents = previousPointerEvents[index];
    });

    for (const element of elements) {
        const preferredElement = getPreferredElement(element, selectionRoot, ignoredElements);

        if (preferredElement) {
            return preferredElement;
        }
    }

    return null;
};

const SelectionOverlay = ({ hoverFrame, selectionFrame, onClick, onMouseMove }: SelectionOverlayProps) =>
    selectionFrame &&
    createPortal(
        <div
            className='position-fixed cursor-crosshair'
            style={{
                left: selectionFrame.left,
                top: selectionFrame.top,
                width: selectionFrame.width,
                height: selectionFrame.height,
                zIndex: 2147483646,
            }}
            onMouseMove={onMouseMove}
            onClick={onClick}
        >
            {hoverFrame && (
                <div
                    className='position-fixed pointer-events-none border border-width-2 border-color-primary'
                    style={{
                        left: hoverFrame.left,
                        top: hoverFrame.top,
                        width: hoverFrame.width,
                        height: hoverFrame.height,
                    }}
                />
            )}
        </div>,
        document.body
    );

export const PlaygroundDetachDialog = ({
    children,
    contentClassName,
    onClose,
    reportContext,
    show,
}: PlaygroundDetachDialogProps) => {
    const contentRef = useRef<HTMLDivElement>(null);
    const footerRef = useRef<HTMLDivElement>(null);
    const overlayRef = useRef<HTMLElement | null>(null);
    const selectedElementRef = useRef<HTMLElement | null>(null);
    const clipboard = useClipboard();
    const [hoverFrame, setHoverFrame] = useState<HoverFrame | null>(null);
    const [selectionFrame, setSelectionFrame] = useState<HoverFrame | null>(null);
    const [selectionMode, setSelectionMode] = useState<SelectionMode | null>(null);
    const [clipboardSuccessMessage, setClipboardSuccessMessage] = useState('Copied element');
    const [showHowToDialog, setShowHowToDialog] = useState(false);
    const [showSupportDialog, setShowSupportDialog] = useState(false);
    const [status, setStatus] = useState('');

    const isSelecting = selectionMode !== null;
    const pageUrl =
        reportContext?.sectionUrl ??
        reportContext?.pageUrl ??
        (typeof window === 'undefined' ? '' : window.location.href);
    const pluginReportMessage =
        typeof window === 'undefined'
            ? ''
            : ['Figma import issue', `${pageUrl}`, '', 'What went wrong?', '- ']
                  .filter((line): line is string => line !== null)
                  .join('\n');

    useEffect(() => {
        clipboard.copied && Notification.default(clipboardSuccessMessage, { timeout: 3000 });
    }, [clipboard.copied, clipboardSuccessMessage]);

    useEffect(() => {
        if (clipboard.error) {
            setStatus('Clipboard access was blocked.');
        }
    }, [clipboard.error]);

    useEffect(() => {
        if (!isSelecting) {
            return;
        }

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                cancelSelection();
            }
        };

        const handlePageChanged = () => {
            setHoverFrame(null);
            updateSelectionFrame();
        };

        document.addEventListener('keydown', handleKeyDown, true);
        window.addEventListener('scroll', handlePageChanged, true);
        window.addEventListener('resize', handlePageChanged, true);

        return () => {
            document.removeEventListener('keydown', handleKeyDown, true);
            window.removeEventListener('scroll', handlePageChanged, true);
            window.removeEventListener('resize', handlePageChanged, true);
        };
    }, [isSelecting]);

    const resetSelection = () => {
        setSelectionMode(null);
        setHoverFrame(null);
        setSelectionFrame(null);
        selectedElementRef.current = null;
    };

    const startSelection = (mode: SelectionMode) => {
        clipboard.reset();
        setClipboardSuccessMessage('Copied element');
        setSelectionMode(mode);
        updateSelectionFrame();
        setStatus('Select an element to copy it. Press Esc to cancel.');
    };

    const cancelSelection = () => {
        resetSelection();
        setStatus('Selection cancelled.');
    };

    const closeDialog = () => {
        resetSelection();
        clipboard.reset();
        setShowHowToDialog(false);
        setShowSupportDialog(false);
        setStatus('');
        onClose();
    };

    const getIgnoredElements = () => [overlayRef.current].filter((element): element is HTMLElement => element !== null);

    const updateSelectionFrame = () => {
        const contentRect = contentRef.current?.getBoundingClientRect();

        if (!contentRect) {
            setSelectionFrame(null);
            return;
        }

        const footerTop = footerRef.current?.getBoundingClientRect().top ?? window.innerHeight;
        const left = Math.max(contentRect.left, 0);
        const top = Math.max(contentRect.top, 0);
        const right = Math.min(contentRect.right, window.innerWidth);
        const bottom = Math.min(contentRect.bottom, footerTop, window.innerHeight);
        const width = right - left;
        const height = bottom - top;

        setSelectionFrame(width > 0 && height > 0 ? { left, top, width, height } : null);
    };

    const handleSelectionMouseMove = (event: MouseEvent<HTMLDivElement>) => {
        overlayRef.current = event.currentTarget;

        const element = getElementFromPoint(event.clientX, event.clientY, contentRef.current, getIgnoredElements());

        selectedElementRef.current = element;

        if (!element) {
            setHoverFrame(null);
            return;
        }

        const rect = element.getBoundingClientRect();

        setHoverFrame({
            left: rect.left,
            top: rect.top,
            width: rect.width,
            height: rect.height,
        });
    };

    const copyElementForFigma = async (element: HTMLElement) => {
        setStatus('Preparing Figma payload...');
        setClipboardSuccessMessage('Copied element');

        try {
            clipboard.copy(await getFigmaPayloadFromElement(element));
            resetSelection();
            setStatus('');
        } catch {
            setStatus('Could not prepare Figma payload.');
        }
    };

    const copyElementForWeb = async (element: HTMLElement) => {
        setStatus('Preparing HTML payload...');
        setClipboardSuccessMessage('Copied element');

        try {
            clipboard.copy(await getWebHtmlFromElement(element));
            resetSelection();
            setStatus('');
        } catch {
            setStatus('Could not prepare HTML payload.');
        }
    };

    const handleSelectionClick = (event: MouseEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();

        const element =
            selectedElementRef.current ??
            getElementFromPoint(event.clientX, event.clientY, contentRef.current, getIgnoredElements());

        if (element && selectionMode === 'figma') {
            void copyElementForFigma(element);
        } else if (element && selectionMode === 'web') {
            void copyElementForWeb(element);
        }
    };

    const body = (
        <div className='height-100pct bg-lighter padding-20'>
            <div ref={contentRef} className={`${contentClassName}`}>
                <div className='detached-view'>{children}</div>
            </div>
        </div>
    );

    const howToDialogBody = (
        <>
            <p className='text-wrap-balance text-size-16 margin-top-0'>
                Install the plugin once from the downloaded zip, keep the extracted folder locally, then load its
                manifest in Figma.
            </p>
            <ol>
                <li>Download the plugin zip.</li>
                <li>Extract it to a local folder that you keep, for example in your Downloads or Documents folder.</li>
                <li>Do not move or delete that extracted folder after importing it into Figma.</li>
                <li>
                    In Figma, open <strong>Plugins</strong>, choose <strong>Development</strong>, then{' '}
                    <strong>Import plugin from manifest…</strong> and select the <code>manifest.json</code> file from
                    the extracted folder.
                </li>
            </ol>
            <p className='margin-bottom-0'>
                If the plugin stops working later, check whether the extracted folder still exists in the same location.
            </p>
        </>
    );

    return (
        <>
            <Dialog
                show={show}
                title='Figma export'
                subtitle='Download the plugin, inspect the rendered component, then select an element to copy it for Figma.'
                headerButtons={
                    <ButtonToolbar className='flex-nowrap'>
                        <Button
                            className='margin-right-0'
                            bsStyle={Button.MUTED}
                            iconName='download'
                            iconOnly
                            onClick={() => setShowHowToDialog(true)}
                            tooltip='Download and install the Figma plugin'
                        />
                        <Button
                            bsStyle={Button.MUTED}
                            iconName='warning-sign'
                            iconOnly
                            onClick={() => setShowSupportDialog(true)}
                            tooltip='Report broken figma import'
                        />
                    </ButtonToolbar>
                }
                body={body}
                footer={
                    <div ref={footerRef} className='display-flex justify-content-between align-items-center'>
                        <span className='text-color-darker'>{status}</span>
                        <ButtonToolbar>
                            {isSelecting ? (
                                <Button bsStyle={Button.WARNING} iconName='remove' onClick={cancelSelection}>
                                    Cancel selection
                                </Button>
                            ) : (
                                <>
                                    <Button onClick={closeDialog}>Close</Button>
                                    <Button
                                        bsStyle={Button.PRIMARY}
                                        iconName='files'
                                        onClick={() => startSelection('figma')}
                                    >
                                        Copy for Figma
                                    </Button>
                                    {isDev && (
                                        <Button
                                            bsStyle={Button.SECONDARY}
                                            iconName='code-brackets'
                                            onClick={() => startSelection('web')}
                                        >
                                            Copy for Web
                                        </Button>
                                    )}
                                </>
                            )}
                        </ButtonToolbar>
                    </div>
                }
                bodyClassName='padding-0 bg-white'
                bsSize={Dialog.SIZE_FULL_SCREEN}
                disableEsc={isSelecting}
                onClose={closeDialog}
                showCloseButton
                useOverflow
            />
            <Dialog
                show={showHowToDialog}
                title='How to install the Figma plugin'
                subtitle='Download it once and keep the extracted folder.'
                body={howToDialogBody}
                footer={
                    <ButtonToolbar>
                        <Button onClick={() => setShowHowToDialog(false)}>Close</Button>
                        <Button
                            as='a'
                            bsStyle={Button.PRIMARY}
                            download='uikit-figma-plugin.zip'
                            href={getFigmaPluginDownloadUrl()}
                            iconName='download'
                        >
                            Download plugin
                        </Button>
                    </ButtonToolbar>
                }
                bsSize={Dialog.SIZE_SM}
                onClose={() => setShowHowToDialog(false)}
                showCloseButton
            />
            <ReportDialog
                show={showSupportDialog}
                title='Report broken figma import'
                subtitle='Copy the report and post it in Slack.'
                bodyIntro='Update the prepared report text, then open the Slack channel and paste it there.'
                initialValue={pluginReportMessage}
                linkButtonHref={figmaPluginSupportUrl}
                linkButtonLabel='Open Slack'
                onClose={() => setShowSupportDialog(false)}
            />
            {isSelecting && (
                <SelectionOverlay
                    hoverFrame={hoverFrame}
                    selectionFrame={selectionFrame}
                    onClick={handleSelectionClick}
                    onMouseMove={handleSelectionMouseMove}
                />
            )}
        </>
    );
};
